import { motion } from "motion/react";
import { daysToDeadline, entryDuration, formatMoney, isLive, projectColor, projectTotals, runningEntry, startTimer, stopTimer, type Dataset, type Project, type TimeEntry } from "@otrack/domain";
import { IconButton } from "@otrack/ui";
import { billingSummary } from "./billing-labels";
import { DayBars } from "./components";
import { useApp, useDataset, useFactory } from "./context";
import { href } from "./router";
import { formatDuration, formatMonth, monthRange, startOfDay } from "./time";

/** Time tracked on each of the last `days` days (oldest first), by entry start, for matching entries. */
export function dailyHours(dataset: Dataset, now: number, days: number, match: (entry: TimeEntry) => boolean): number[] {
  const from = startOfDay(now, -(days - 1));
  const totals = new Array<number>(days).fill(0);
  for (const entry of Object.values(dataset.timeEntries)) {
    if (!isLive(entry) || entry.start < from || !match(entry)) continue;
    const index = Math.round((startOfDay(entry.start) - from) / 86_400_000);
    if (index >= 0 && index < days) totals[index]! += entryDuration(entry, now);
  }
  return totals;
}

/** "Due in 3 days", "Due today", "2 days past deadline", from `daysToDeadline`. */
export function deadlineLabel(days: number): string {
  if (days === 0) return "Due today";
  if (days === 1) return "Due tomorrow";
  if (days > 1) return `Due in ${days} days`;
  return `${-days} ${days === -1 ? "day" : "days"} past deadline`;
}

export function ProjectCard({ project, index = 0 }: { project: Project; index?: number }) {
  const { now, act } = useApp();
  const dataset = useDataset();
  const factory = useFactory();
  const month = projectTotals(dataset, project.id, now, monthRange(now));
  const allTime = projectTotals(dataset, project.id, now);
  const running = runningEntry(dataset);
  const live = running?.projectId === project.id;
  const recent = dailyHours(dataset, now, 14, (entry) => entry.projectId === project.id);
  const due = daysToDeadline(project, now);
  const status = project.archivedAt ? "Archived" : project.completedAt ? "Delivered" : due !== null ? deadlineLabel(due) : null;

  return <motion.article
    layout="position"
    className={`ot-pcard ${live ? "is-live" : ""} ${project.archivedAt ? "is-archived" : ""}`}
    style={{ ["--pc" as string]: `var(--ot-pc-${projectColor(project)})` }}
    initial={{ opacity: 0, y: 12, scale: 0.98 }}
    animate={{ opacity: 1, y: 0, scale: 1 }}
    exit={{ opacity: 0, scale: 0.96, transition: { duration: 0.16 } }}
    transition={{ duration: 0.45, ease: [0.16, 1, 0.3, 1], delay: Math.min(index, 10) * 0.035 }}
  >
    <a className="ot-pcard__link" href={href({ page: "project", id: project.id })} aria-label={`${project.name}, ${billingSummary(dataset, project)}`}/>
    <div className="ot-pcard__head">
      <div className="ot-pcard__titles">
        <strong><i className="ot-swatch" aria-hidden="true"/>{project.name}</strong>
        <span>{live ? <><span className="ot-chip__live" aria-hidden="true"/>Running now</> : billingSummary(dataset, project)}{status ? ` · ${status}` : ""}</span>
      </div>
      {live
        ? <IconButton className="ot-pcard__action is-live" label={`Stop ${project.name}`} icon="stop" tooltip="Stop timer" onClick={() => void act(stopTimer(dataset, factory, Date.now()), `Stopped ${project.name}`)}/>
        : !project.archivedAt && <IconButton className="ot-pcard__action" label={`Start ${project.name}`} icon="play" tooltip="Start timer" onClick={() => void act(startTimer(dataset, { projectId: project.id }, factory, Date.now()), `Started ${project.name}`)}/>}
    </div>
    <DayBars days={recent} end={now} compact label={`Last 14 days on ${project.name}`}/>
    <dl className="ot-pcard__facts">
      <div><dt>{formatMonth(now)}</dt><dd className="ot-mono">{formatDuration(month.tracked)}</dd></div>
      <div><dt>Earned</dt><dd className="ot-mono">{formatMoney({ amount: month.earned, currency: month.currency })}</dd></div>
      <div><dt>All time</dt><dd className="ot-mono">{formatDuration(allTime.tracked)}</dd></div>
    </dl>
  </motion.article>;
}
